import type { DuctedPanelSelection } from 'components/equipment-page/ducted-system-detail-panel';
import {
  DuctedComparedSystem,
  normalizeModel
} from './ducted-compare-identity';
import {
  DuctedSlotValue,
  DuctedSlotValues,
  DuctedSwapBadge,
  DuctedSwapChange,
  DuctedSwapCondenser,
  DuctedSwapLocks,
  DuctedSwapOption,
  DuctedSwapOptionsBySlot,
  DuctedSwapResolution,
  DuctedSwapSlot
} from './ducted-component-swap-types';
import {
  distinguishingBadgeKinds,
  ductedSwapBadges
} from './ducted-swap-badges';

export const DUCTED_SWAP_SLOTS: readonly DuctedSwapSlot[] = [
  'outdoor',
  'indoor',
  'blower'
];

export const DUCTED_SWAP_SLOT_LABELS: Record<DuctedSwapSlot, string> = {
  outdoor: 'Outdoor unit',
  indoor: 'Indoor unit',
  blower: 'Furnace/blower'
};

export const DUCTED_SWAP_SLOT_SUBJECTS: Record<DuctedSwapSlot, string> = {
  outdoor: 'Outdoor Unit',
  indoor: 'Indoor Unit',
  blower: 'Furnace/Blower'
};

const GENERIC_MANUFACTURER = 'Generic';

const KEPT_SLOT_VALUE: DuctedSlotValue = {
  key: '',
  manufacturer: '',
  modelNumber: ''
};

const SLOT_FIELDS: Record<
  DuctedSwapSlot,
  { manufacturer: string; model: string }
> = {
  outdoor: {
    manufacturer: 'OutdoorManufacturer',
    model: 'OutdoorModelNumber'
  },
  indoor: {
    manufacturer: 'IndoorManufacturer',
    model: 'IndoorModelNumber'
  },
  blower: {
    manufacturer: 'BlowerManufacturer',
    model: 'BlowerModelNumber'
  }
};

const emptyOptions = (): DuctedSwapOptionsBySlot => ({
  outdoor: [],
  indoor: [],
  blower: []
});

const combinationSlotValue = (
  combination: Record<string, unknown>,
  slot: DuctedSwapSlot
): DuctedSlotValue => {
  const fields = SLOT_FIELDS[slot];
  const manufacturer = combination[fields.manufacturer];
  const modelNumber = combination[fields.model];
  if (
    typeof manufacturer !== 'string' ||
    typeof modelNumber !== 'string' ||
    manufacturer === GENERIC_MANUFACTURER ||
    modelNumber.trim().length === 0
  ) {
    return KEPT_SLOT_VALUE;
  }
  return { key: normalizeModel(modelNumber), manufacturer, modelNumber };
};

export const combinationSlotValues = (
  combination: Record<string, unknown>
): DuctedSlotValues => ({
  outdoor: combinationSlotValue(combination, 'outdoor'),
  indoor: combinationSlotValue(combination, 'indoor'),
  blower: combinationSlotValue(combination, 'blower')
});

const systemSlotValue = (model: unknown): DuctedSlotValue => {
  if (typeof model !== 'string') return KEPT_SLOT_VALUE;
  const modelNumber = model.trim();
  const key = normalizeModel(modelNumber);
  if (key === '') return KEPT_SLOT_VALUE;
  return { key, manufacturer: '', modelNumber };
};

export const condenserSlotValues = (
  condenser: DuctedSwapCondenser | null | undefined
): DuctedSlotValues => {
  const zone = condenser?.zones?.[0];
  return {
    outdoor: systemSlotValue(condenser?.selectedProduct?.model),
    indoor: systemSlotValue(zone?.selectedProduct?.model),
    blower: systemSlotValue(zone?.blowerProduct?.model)
  };
};

export const systemSlotValues = (
  system: DuctedComparedSystem
): DuctedSlotValues =>
  condenserSlotValues(system.configuration?.condensers?.[0]);

export const isDuctedSlotLocked = (
  slot: DuctedSwapSlot,
  current: DuctedSlotValues,
  locks: DuctedSwapLocks
): boolean => locks[slot] || current[slot].key === '';

const otherSlots = (slot: DuctedSwapSlot): DuctedSwapSlot[] =>
  DUCTED_SWAP_SLOTS.filter((candidate) => candidate !== slot);

const holdsLockedSlots = (
  values: DuctedSlotValues,
  current: DuctedSlotValues,
  locks: DuctedSwapLocks,
  swappingSlot: DuctedSwapSlot
): boolean =>
  otherSlots(swappingSlot).every(
    (slot) =>
      !isDuctedSlotLocked(slot, current, locks) ||
      values[slot].key === current[slot].key
  );

const changedSlots = (
  values: DuctedSlotValues,
  current: DuctedSlotValues,
  swappingSlot: DuctedSwapSlot
): DuctedSwapChange[] =>
  otherSlots(swappingSlot)
    .filter((slot) => values[slot].key !== current[slot].key)
    .map((slot) => ({
      slot,
      fromModel: current[slot].modelNumber,
      toModel: values[slot].modelNumber
    }));

const slotBadges = (
  slot: DuctedSwapSlot,
  value: DuctedSlotValue,
  selection: DuctedPanelSelection
): DuctedSwapBadge[] =>
  ductedSwapBadges(
    slot,
    selection.modelSpecs?.[`${value.manufacturer}|${value.modelNumber}`],
    selection.combination
  );

const buildOption = (
  slot: DuctedSwapSlot,
  value: DuctedSlotValue,
  selection: DuctedPanelSelection
): DuctedSwapOption => {
  const key = `${value.manufacturer}|${value.modelNumber}`;
  const product = selection.productData[key];
  return {
    key,
    modelKey: value.key,
    slot,
    manufacturer: value.manufacturer,
    modelNumber: value.modelNumber,
    modelText: `${value.manufacturer} ${value.modelNumber}`,
    product,
    badges: slotBadges(slot, value, selection),
    price: product?.price ?? null,
    selection
  };
};

const currentSlotBadges = (
  candidates: DuctedPanelSelection[],
  current: DuctedSlotValues,
  slot: DuctedSwapSlot
): DuctedSwapBadge[] | null => {
  if (current[slot].key === '') return null;
  const holders = candidates
    .map((candidate) => ({
      candidate,
      values: combinationSlotValues(candidate.combination)
    }))
    .filter(({ values }) => values[slot].key === current[slot].key);
  if (holders.length === 0) return null;
  const exact = holders.find(({ values }) =>
    DUCTED_SWAP_SLOTS.every((other) => values[other].key === current[other].key)
  );
  const { candidate, values } = exact ?? holders[0];
  return slotBadges(slot, values[slot], candidate);
};

const withDistinguishingBadges = (
  built: DuctedSwapOption[],
  currentBadges: DuctedSwapBadge[] | null
): DuctedSwapOption[] => {
  const visibleKinds = distinguishingBadgeKinds(
    built.map((option) => option.badges),
    currentBadges
  );
  return built.map((option) => ({
    ...option,
    badges: option.badges.filter((badge) => visibleKinds.has(badge.kind))
  }));
};

const compareOptions = (a: DuctedSwapOption, b: DuctedSwapOption): number => {
  if (a.price !== null && b.price !== null && a.price !== b.price) {
    return a.price - b.price;
  }
  if (a.price === null && b.price !== null) return 1;
  if (a.price !== null && b.price === null) return -1;
  return a.modelText.localeCompare(b.modelText);
};

export function deriveDuctedSwapOptions(
  candidates: DuctedPanelSelection[],
  current: DuctedSlotValues,
  locks: DuctedSwapLocks
): DuctedSwapOptionsBySlot {
  const options = emptyOptions();
  DUCTED_SWAP_SLOTS.forEach((slot) => {
    if (current[slot].key === '') return;
    const byModel = new Map<
      string,
      { option: DuctedSwapOption; exact: boolean }
    >();
    candidates.forEach((candidate) => {
      const values = combinationSlotValues(candidate.combination);
      const value = values[slot];
      if (value.key === '' || value.key === current[slot].key) return;
      if (!holdsLockedSlots(values, current, locks, slot)) return;
      const exact = changedSlots(values, current, slot).length === 0;
      const existing = byModel.get(value.key);
      if (existing && (existing.exact || !exact)) return;
      byModel.set(value.key, {
        option: buildOption(slot, value, candidate),
        exact
      });
    });
    options[slot] = withDistinguishingBadges(
      Array.from(byModel.values())
        .map((entry) => entry.option)
        .sort(compareOptions),
      currentSlotBadges(candidates, current, slot)
    );
  });
  return options;
}

export function resolveDuctedSwap(
  candidates: DuctedPanelSelection[],
  current: DuctedSlotValues,
  slot: DuctedSwapSlot,
  modelKey: string,
  locks: DuctedSwapLocks
): DuctedSwapResolution | null {
  let best: DuctedSwapResolution | null = null;
  candidates.forEach((candidate) => {
    const values = combinationSlotValues(candidate.combination);
    if (values[slot].key !== modelKey) return;
    if (!holdsLockedSlots(values, current, locks, slot)) return;
    const changes = changedSlots(values, current, slot);
    if (best === null || changes.length < best.changes.length) {
      best = { selection: candidate, changes };
    }
  });
  return best;
}

const describeModel = (model: string): string =>
  model === '' ? "customer's existing unit" : model;

export function describeDuctedSwapChanges(changes: DuctedSwapChange[]): string {
  return changes
    .map(
      (change) =>
        `${DUCTED_SWAP_SLOT_LABELS[change.slot]} changed: ${describeModel(
          change.fromModel
        )} → ${describeModel(change.toModel)}`
    )
    .join('\n');
}
